import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  TextField,
  Button,
  Box,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { CheckCircle, Person, Psychology, Group } from '@mui/icons-material';
import { debounce } from 'lodash';
import {
  PatientAssessment,
  AssessmentTemplate,
  assessmentAPI,
  calculateProgress,
  isAssessmentComplete,
} from '../../services/assessmentService';

interface BiopsychosocialAssessmentProps {
  assessment: PatientAssessment;
  template: AssessmentTemplate;
  onComplete: () => void;
  onError: (error: string) => void;
}

const BiopsychosocialAssessment: React.FC<BiopsychosocialAssessmentProps> = ({
  assessment,
  template,
  onComplete,
  onError
}) => {
  const [responses, setResponses] = useState<Record<string, string>>(assessment.responses || {});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(assessment.progress || 0);

  // Auto-save debounced function
  const debouncedSave = useCallback(
    debounce(async (responses: Record<string, string>, progress: number) => {
      try {
        setSaving(true);
        await assessmentAPI.saveResponses(assessment.assessmentId, {
          responses,
          progress,
          status: progress > 0 ? 'in_progress' : 'assigned'
        });
      } catch (error) {
      } finally {
        setSaving(false);
      }
    }, 2000),
    [assessment.assessmentId]
  );

  // Update progress when responses change
  useEffect(() => {
    const newProgress = calculateProgress(responses, template);
    setProgress(newProgress);

    // Auto-save if there are responses
    if (Object.keys(responses).length > 0) {
      debouncedSave(responses, newProgress);
    }
  }, [responses, template, debouncedSave]);

  const handleResponseChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Validate all required questions are answered
      if (!isAssessmentComplete(responses, template)) {
        onError('Please answer all required questions before submitting.');
        return;
      }

      // Submit assessment without scoring (biopsychosocial doesn't have numerical scoring)
      await assessmentAPI.submitAssessment(assessment.assessmentId, {
        responses
      });

      onComplete();
    } catch (error: any) {
      onError(error.response?.data?.message || 'Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: any, index: number) => {
    const value = responses[question.questionId] || '';

    switch (question.questionType) {
      case 'long_text':
        return (
          <FormControl key={question.questionId} component="fieldset" sx={{ mb: 4, width: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {index + 1}. {question.questionText}
                {question.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
              </Typography>
              {question.helpText && (
                <Typography variant="body2" color="text.secondary">
                  {question.helpText}
                </Typography>
              )}
            </Box>
            <TextField
              multiline
              rows={4}
              value={value}
              onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
              placeholder="Please provide detailed information..."
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            />
          </FormControl>
        );

      case 'multiple_choice':
        return (
          <FormControl key={question.questionId} component="fieldset" sx={{ mb: 4, width: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {index + 1}. {question.questionText}
                {question.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
              </Typography>
              {question.helpText && (
                <Typography variant="body2" color="text.secondary">
                  {question.helpText}
                </Typography>
              )}
            </Box>
            <RadioGroup
              value={value}
              onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
              sx={{ pl: 2 }}
            >
              {question.options?.map((option: string, idx: number) => (
                <FormControlLabel
                  key={idx}
                  value={option}
                  control={<Radio />}
                  label={option}
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    border: value === option ? '2px solid' : '1px solid transparent',
                    borderColor: value === option ? 'primary.main' : 'transparent',
                    bgcolor: value === option ? 'primary.50' : 'transparent',
                    '&:hover': {
                      bgcolor: 'grey.50'
                    }
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      default:
        return (
          <FormControl key={question.questionId} component="fieldset" sx={{ mb: 4, width: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {index + 1}. {question.questionText}
                {question.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
              </Typography>
              {question.helpText && (
                <Typography variant="body2" color="text.secondary">
                  {question.helpText}
                </Typography>
              )}
            </Box>
            <TextField
              value={value}
              onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            />
          </FormControl>
        );
    }
  };

  const getQuestionsPerSection = () => {
    const total = template.questions.length;
    return Math.ceil(total / 3); // Divide into 3 sections
  };

  const questionsPerSection = getQuestionsPerSection();
  const biologicalQuestions = template.questions.slice(0, questionsPerSection);
  const psychologicalQuestions = template.questions.slice(questionsPerSection, questionsPerSection * 2);
  const socialQuestions = template.questions.slice(questionsPerSection * 2);

  const isComplete = isAssessmentComplete(responses, template);

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Person sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" gutterBottom>
              Biopsychosocial Assessment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive evaluation of your biological, psychological, and social factors
            </Typography>
          </Box>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress: {Math.round(progress * 100)}%
            </Typography>
            {saving && (
              <Typography variant="caption" color="text.secondary">
                Saving...
              </Typography>
            )}
          </Box>
          <LinearProgress variant="determinate" value={progress * 100} sx={{ height: 6, borderRadius: 3 }} />
        </Box>

        {/* Biological Factors Section */}
        {biologicalQuestions.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'error.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Psychology sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" color="error.main">
                  Biological Factors
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Questions about your physical health, medical history, and biological factors
              </Typography>
            </Paper>
            {biologicalQuestions.map((question, index) => renderQuestion(question, index))}
          </Box>
        )}

        {/* Psychological Factors Section */}
        {psychologicalQuestions.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Psychology sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6" color="warning.main">
                  Psychological Factors
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Questions about your mental health, emotions, and psychological well-being
              </Typography>
            </Paper>
            {psychologicalQuestions.map((question, index) => 
              renderQuestion(question, index + biologicalQuestions.length)
            )}
          </Box>
        )}

        {/* Social Factors Section */}
        {socialQuestions.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Group sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6" color="info.main">
                  Social Factors
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Questions about your social support, relationships, and environmental factors
              </Typography>
            </Paper>
            {socialQuestions.map((question, index) => 
              renderQuestion(question, index + biologicalQuestions.length + psychologicalQuestions.length)
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isComplete && (
              <CheckCircle color="success" fontSize="small" />
            )}
            <Typography variant="body2" color={isComplete ? 'success.main' : 'text.secondary'}>
              {isComplete 
                ? 'All questions answered' 
                : `${template.questions.length - Object.keys(responses).length} questions remaining`
              }
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!isComplete || submitting}
            startIcon={submitting ? null : <CheckCircle />}
            sx={{ minWidth: 200 }}
          >
            {submitting ? 'Submitting...' : 'Complete Assessment'}
          </Button>
        </Box>

        {/* Help Text */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>About Biopsychosocial Assessment:</strong> This comprehensive evaluation examines biological, 
            psychological, and social factors that may impact your health condition. Your detailed responses help 
            healthcare providers understand the full context of your situation and develop appropriate treatment recommendations.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BiopsychosocialAssessment;